import { useMemo } from 'react'
import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import type { OrderEventType } from '@klear/order-event/constants'
import type { TimelineEventFormValues } from '@klear/order-event/event-form'
import { trpc } from '@klear/ui/lib/trpc'
import {
  createInvoiceFormSchema,
  createOperationalFormSchema,
  createProofOfAcceptanceFormSchema,
  createPurchaseOrderFormSchema,
  invoiceReadToFormSchema,
  operationalReadToFormSchema,
  proofOfAcceptanceReadToFormSchema,
  purchaseOrderReadToFormSchema
} from '@klear/order-event-ui/event-shared/event-form-read-mappers'
import { useOrderEventDocuments } from '@klear/ui/hooks/use-order-event-documents'
import { useEventFormParentDefaults } from '@klear/order-event-ui/event-shared/use-event-form-parent-defaults'

export type EventFormBootstrap = {
  editEventId: number | null
  originalDocumentIds: string[]
  originalFileUrls?: string[]
  formValues: TimelineEventFormValues
  parentEventId?: number
  payableId?: number
  paymentParentSupplierId?: string | number
  paymentStripePaymentId?: string
}

export function useEventFormBootstrap({
  eventType,
  editEventId,
  parentEventId,
  traderId
}: {
  eventType: OrderEventType
  editEventId?: number
  parentEventId?: number
  traderId: number
}): { bootstrap: EventFormBootstrap | null; isLoading: boolean } {
  const editId = editEventId != null && editEventId > 0 ? editEventId : null

  const { parentId, parentDefaults, isLoading: parentLoading } = useEventFormParentDefaults({ parentEventId, traderId })

  const { documentsFor, isFetching: documentsFetching } = useOrderEventDocuments(
    editId == null ? [] : [editId],
    traderId
  )
  const editDocuments = documentsFor(editId)

  const editPoQuery = trpc.purchaseOrder.get.useQuery(
    { id: editId ?? 0, traderId },
    { enabled: eventType === PERMISSION_ENTITY_PURCHASE_ORDER && editId != null }
  )
  const editInvoiceQuery = trpc.invoice.get.useQuery(
    { id: editId ?? 0, traderId },
    { enabled: eventType === PERMISSION_ENTITY_INVOICE && editId != null }
  )
  const editOperationalQuery = trpc.operational.get.useQuery(
    { id: editId ?? 0, traderId },
    { enabled: eventType === PERMISSION_ENTITY_OPERATIONAL && editId != null }
  )
  const editProofQuery = trpc.proofOfAcceptance.get.useQuery(
    { id: editId ?? 0, traderId },
    { enabled: eventType === PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE && editId != null }
  )

  const bootstrap = useMemo((): EventFormBootstrap | null => {
    let formValues: TimelineEventFormValues

    switch (eventType) {
      case PERMISSION_ENTITY_PURCHASE_ORDER:
        if (editId != null) {
          if (!editPoQuery.data) return null
          formValues = purchaseOrderReadToFormSchema(editPoQuery.data)
        } else {
          formValues = createPurchaseOrderFormSchema()
        }
        break
      case PERMISSION_ENTITY_INVOICE:
        if (editId != null) {
          if (!editInvoiceQuery.data) return null
          formValues = invoiceReadToFormSchema(editInvoiceQuery.data)
        } else {
          formValues = createInvoiceFormSchema(parentDefaults)
        }
        break
      case PERMISSION_ENTITY_OPERATIONAL:
        if (editId != null) {
          if (!editOperationalQuery.data) return null
          formValues = operationalReadToFormSchema(editOperationalQuery.data)
        } else {
          formValues = createOperationalFormSchema(parentDefaults)
        }
        break
      case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
        if (editId != null) {
          if (!editProofQuery.data) return null
          formValues = proofOfAcceptanceReadToFormSchema(editProofQuery.data)
        } else {
          formValues = createProofOfAcceptanceFormSchema()
        }
        break
      default:
        return null
    }

    return {
      editEventId: editId,
      originalDocumentIds: editDocuments.map((document) => document.id),
      formValues: { ...formValues, documents: editDocuments },
      parentEventId: parentId ?? undefined
    }
  }, [
    eventType,
    editId,
    parentId,
    parentDefaults,
    editPoQuery.data,
    editInvoiceQuery.data,
    editOperationalQuery.data,
    editProofQuery.data,
    editDocuments
  ])

  const isLoading =
    documentsFetching ||
    parentLoading ||
    (eventType === PERMISSION_ENTITY_PURCHASE_ORDER && editId != null && editPoQuery.isFetching) ||
    (eventType === PERMISSION_ENTITY_INVOICE && editId != null && editInvoiceQuery.isFetching) ||
    (eventType === PERMISSION_ENTITY_OPERATIONAL && editId != null && editOperationalQuery.isFetching) ||
    (eventType === PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE && editId != null && editProofQuery.isFetching)

  return { bootstrap, isLoading }
}
